import URI from "urijs";
import { Envs, MBPLandingParams, UserLanguage } from "../@types";
import { UserToQueryStringLangMap } from "./CONSTANTS";

export const getEnvironment = (): Envs => {
  const hostname = window.location.hostname;
  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return Envs.local;
  }

  if (hostname.includes("test")) {
    return Envs.test;
  }

  if (hostname.includes("imp")) {
    return Envs.imp;
  }

  return Envs.prod;
};

export const parseDegradedIntegrations = (headers: Headers): string[] => {
  const integrations = headers.get("failed-integrations") || "";

  return integrations
    .trim()
    .split(",")
    .map(x => x.trim())
    .filter(x => !!x);
};

export enum SlsxUrlAction {
  AUTHORIZE = "authorize",
  SIGNOUT = "signout",
}

export const formatSlsxUrl = (env: Envs, action: SlsxUrlAction): string => {
  const templateUrl = "https://{env}medicare.gov/sso/{action}";

  const urlReplacements = {
    [Envs.local]: "test",
    [Envs.test]: "test",
    [Envs.imp]: "imp",
    [Envs.prod]: "",
  };

  const urlReplacement = urlReplacements[env];

  return templateUrl
    .replace("{env}", urlReplacement ? `${urlReplacement}.` : "")
    .replace("{action}", action);
};

export const getSlsxSignoutUrl = ({
  queriesToAppend,
  redirectLocalToTest = false,
}: {
  queriesToAppend?: Record<string, string>;
  redirectLocalToTest?: boolean;
} = {}): string => {
  const env = getEnvironment();
  // Unless SLSx updates rules, redirect to localhost does not work, so redirect
  // from signout on localhost to test
  let domain = "";
  switch (env) {
    case Envs.local:
      domain = Envs.test;
      break;
    case Envs.prod:
      domain = "www";
      break;
    default:
      domain = env;
  }

  const baseUri = `${
    env === Envs.local && !redirectLocalToTest
      ? "localhost"
      : `${domain}.medicare.gov/plan-compare`
  }`;

  let redirectUri = `https://${baseUri}${queriesToAppend ? "/#/" : ""}`;

  if (queriesToAppend) {
    Object.entries(queriesToAppend).forEach((kv, i) => {
      redirectUri += `${i === 0 ? "?" : "&"}${kv[0]}=${kv[1]}`;
    });
  }

  const signoutUrl = new URI(formatSlsxUrl(env, SlsxUrlAction.SIGNOUT))
    .addQuery("redirect_uri", redirectUri)
    .toString();

  return signoutUrl;
};

export const getLoginParams = (
  env: Envs
): {
  client_id: string;
  mbp_uri: string;
  redirect_uri: string;
} => {
  const clientIds = {
    [Envs.local]: "mct-test",
    [Envs.test]: "mct-test",
    [Envs.imp]: "mct-imp",
    [Envs.prod]: "mct",
  };

  const client_id = clientIds[env];

  const mbp_uri = formatSlsxUrl(env, SlsxUrlAction.AUTHORIZE);

  let redirect_uri = "https://www.medicare.gov/plan-compare/sls-callback";

  if (env === Envs.local) {
    redirect_uri = "https://localhost/sls-callback";
  } else if (env !== Envs.prod) {
    redirect_uri = `https://${env}.medicare.gov/plan-compare/sls-callback`;
  }

  return {
    client_id,
    mbp_uri,
    redirect_uri,
  };
};

export const hasLoginSession = (): boolean => {
  return !!document.cookie && document.cookie.includes("sid");
};

const buildLoginUrl = (
  language: UserLanguage,
  redirectUri = "",
  relay: Record<string, unknown> | MBPLandingParams | undefined = {}
): URI => {
  const env = getEnvironment();

  const { mbp_uri, ...paramsToSend } = getLoginParams(env);

  URI.escapeQuerySpace = false;

  let query: Record<string, unknown> = {
    ...paramsToSend,
    lang: UserToQueryStringLangMap[language],
  };

  if (redirectUri) {
    query = { ...query, redirect_uri: redirectUri };
  }

  const encoded = btoa(
    JSON.stringify({
      ...relay,
      lang: language,
    })
  );

  query = { ...query, relay: encoded };

  return new URI(mbp_uri).addQuery(query);
};

export const initiateLogin = (
  language: UserLanguage,
  redirectUri = "",
  relay: Record<string, unknown> | MBPLandingParams | undefined = undefined
): void => {
  const url = buildLoginUrl(language, redirectUri, relay);

  window.location.assign(url.toString());
};

export const getLoginUrl = (language: UserLanguage): string => {
  const url = buildLoginUrl(language, "", undefined);

  return url.toString();
};

export const getRegistrationUrl = (language: UserLanguage): string => {
  const env = getEnvironment();

  const domainPrefixes = {
    [Envs.local]: "test.",
    [Envs.test]: "test.",
    [Envs.imp]: "imp.",
    [Envs.prod]: "",
  };

  const prefix = domainPrefixes[env];

  const baseURL = "medicare.gov/account/create-account";
  const registrationURL = new URI(`https://${prefix}${baseURL}`);

  registrationURL.addQuery("lang", UserToQueryStringLangMap[language]);

  return registrationURL.toString();
};
